import React, { useState, useEffect, useRef } from 'react';
import './Contact.css';
import emailjs from '@emailjs/browser';

export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    storeType: '', product: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const leftRef = useRef(null);
  const rightRef = useRef(null);

  useEffect(() => {
  // Initialize EmailJS
  emailjs.init('Y2SpiZe-UsLY-Lmxe');

  const obs = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add('visible');
      }),
    { threshold: 0.08 }
  );

  if (leftRef.current) obs.observe(leftRef.current);
  if (rightRef.current) obs.observe(rightRef.current);

  return () => obs.disconnect();
}, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Phone number required';
    if (!form.company.trim()) e.company = 'Business name required';
    return e;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  const errs = validate();

  if (Object.keys(errs).length) {
    setErrors(errs);
    return;
  }

  setLoading(true);

  const templateParams = {
    product_name: 'Mart CMS & POS-Lite',

    full_name: form.name,
    email: form.email,

    company: form.company,

    // Reusing common template fields
    role: form.storeType || 'Not Provided',

    size: form.phone || 'Not Provided',

    interest: form.product || 'Not Provided',

    message: `
Phone Number: ${form.phone}

Store Type: ${form.storeType || 'Not Provided'}

Product Interest: ${form.product || 'Not Provided'}

Store Information:
${form.message || 'No additional details provided'}
    `
  };

  try {
    // Email to Admin
    await emailjs.send(
      'service_ioywtjd',
      'template_exkeoo4',
      templateParams
    );

    // Confirmation email to customer
    await emailjs.send(
      'service_ioywtjd',
      'template_09t0zzd',
      templateParams
    );

    setLoading(false);
    setSubmitted(true);

  } catch (error) {
    console.error('EmailJS Error:', error);
    alert('Unable to send email. Please try again.');
    setLoading(false);
  }
};

  return (
    <section id="contact" style={{ background: '#f9fafb', padding: '100px 0' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 40px' }}>

        <div className="contact-outer-grid">

          {/* LEFT COLUMN */}
          <div ref={leftRef} className="reveal">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '1.8px', textTransform: 'uppercase', color: '#2563eb', marginBottom: 18 }}>
              <span style={{ width: 18, height: 2, background: '#2563eb', borderRadius: 2, display: 'inline-block' }} />
              Get In Touch
            </div>

            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 'clamp(34px,4vw,52px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-1.5px', color: '#0c1220', marginBottom: 20 }}>
              See Mart CMS &amp;<br />POS-Lite in action.
            </h2>

            <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.7, marginBottom: 36, maxWidth: 460 }}>
              Our team will walk you through a personalised demo using your store type and product catalogue. Get all your questions answered in one focused session.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 22, marginBottom: 40 }}>
              {[
                ['📅', 'Personalised 30-min Demo', 'We demo the modules most relevant to your retail type — grocery, food, fashion, or general.'],
                ['⚡', 'Same-Day Response', 'Our onboarding team responds within a few hours on business days.'],
                ['🚀', 'Implementation Support', 'We help you go live — catalogue setup, staff training, and first-order support included.'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.6 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: '#111827', borderRadius: 16, padding: '24px 28px' }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 16 }}>Direct Contact</div>
              {[['📧','info@aiprus.com'],['📞','+91 84200 06452'],['🌐','www.aiprus.com'],['📍','Lucknow, Uttar Pradesh, India']].map(([icon, val]) => (
                <div key={val} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, fontSize: 14, color: 'rgba(255,255,255,0.65)' }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>{val}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN — FORM */}
          <div ref={rightRef} className="reveal">
            <div style={{ background: 'white', borderRadius: 20, padding: '40px', boxShadow: '0 8px 40px rgba(0,0,0,0.10)', border: '1.5px solid #e5e7eb' }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: 60, marginBottom: 20 }}>🎉</div>
                  <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 12, letterSpacing: '-0.5px' }}>
                    Demo Request Received!
                  </h3>
                  <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, marginBottom: 24 }}>
                    Thanks <strong style={{ color: '#111827' }}>{form.name}</strong>! Our team will contact you at <strong style={{ color: '#2563eb' }}>{form.email}</strong> within a few hours.
                  </p>
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '16px 20px', fontSize: 13, color: '#1e40af', textAlign: 'left', lineHeight: 1.6 }}>
                    💡 <strong>What to prepare:</strong> Have a list of your top 10–20 products ready — we'll build a live demo using your actual catalogue.
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 28 }}>
                    <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: '-0.5px', marginBottom: 6 }}>
                      Request a Free Demo
                    </h3>
                    <p style={{ fontSize: 13.5, color: '#9ca3af' }}>Fill in your details and we'll reach out within a few hours.</p>
                  </div>

                  <form onSubmit={handleSubmit} noValidate>

                    <div className="mc-form-row">
                      <div className="mc-form-group">
                        <label className="mc-label">Full Name *</label>
                        <input className={`mc-input${errors.name ? ' mc-input-err' : ''}`} placeholder="Rahul Sharma" value={form.name} onChange={(e) => set('name', e.target.value)} />
                        {errors.name && <p className="mc-err">{errors.name}</p>}
                      </div>
                      <div className="mc-form-group">
                        <label className="mc-label">Email *</label>
                        <input type="email" className={`mc-input${errors.email ? ' mc-input-err' : ''}`} placeholder="rahul@store.com" value={form.email} onChange={(e) => set('email', e.target.value)} />
                        {errors.email && <p className="mc-err">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="mc-form-row">
                      <div className="mc-form-group">
                        <label className="mc-label">Phone Number *</label>
                        <input className={`mc-input${errors.phone ? ' mc-input-err' : ''}`} placeholder="+91 98765 43210" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
                        {errors.phone && <p className="mc-err">{errors.phone}</p>}
                      </div>
                      <div className="mc-form-group">
                        <label className="mc-label">Business Name *</label>
                        <input className={`mc-input${errors.company ? ' mc-input-err' : ''}`} placeholder="Lucknow Mart" value={form.company} onChange={(e) => set('company', e.target.value)} />
                        {errors.company && <p className="mc-err">{errors.company}</p>}
                      </div>
                    </div>

                    <div className="mc-form-row">
                      <div className="mc-form-group">
                        <label className="mc-label">Store Type</label>
                        <select className="mc-input" value={form.storeType} onChange={(e) => set('storeType', e.target.value)}>
                          <option value="">Select type</option>
                          {['Grocery / Kirana','Food & Bakery','Fashion & Apparel','Electronics','Pharmacy','General Retail','Multi-Branch Chain','Franchise Network'].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div className="mc-form-group">
                        <label className="mc-label">Product Interest</label>
                        <select className="mc-input" value={form.product} onChange={(e) => set('product', e.target.value)}>
                          <option value="">Select product</option>
                          {['Mart CMS Only','POS-Lite Only','Both Mart CMS + POS-Lite','Customer App','Porter App','Full Platform'].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="mc-form-group" style={{ marginBottom: 18 }}>
                      <label className="mc-label">Tell us about your store</label>
                      <textarea className="mc-input mc-textarea" placeholder="How many stores do you operate? What challenges are you facing with your current system?" value={form.message} onChange={(e) => set('message', e.target.value)} />
                    </div>

                    <button type="submit" disabled={loading} className="mc-submit-btn">
                      {loading ? (
                        <><span className="mc-spinner" /> Submitting…</>
                      ) : (
                        'Book My Free Demo →'
                      )}
                    </button>

                    <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 12 }}>
                      Your information is secure and will never be shared.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}